/**
 * Alishba Fashions - Collection UI helpers.
 * Handles local collection UI behavior for both:
 * - standalone collection pages
 * - homepage-injected collection shells
 */
(function () {
  'use strict';

  function getCurrencySymbol() {
    return window.AlishbaTheme && window.AlishbaTheme.currencySymbol || '$';
  }

  function updatePriceLabel(root, labelId, minId, maxId) {
    var label = root.querySelector('#' + labelId);
    var minSlider = root.querySelector('#' + minId);
    var maxSlider = root.querySelector('#' + maxId);

    if (!label || !minSlider || !maxSlider) return;

    label.textContent = getCurrencySymbol()
      + parseInt(minSlider.value || 0, 10)
      + ' - '
      + getCurrencySymbol()
      + parseInt(maxSlider.value || 0, 10);
  }

  function refreshPriceLabels(root) {
    updatePriceLabel(root, 'price-label-desktop', 'price-min-desktop', 'price-max-desktop');
    updatePriceLabel(root, 'price-label-mobile', 'price-min-mobile', 'price-max-mobile');
  }

  function refreshPriceFills(root) {
    root.querySelectorAll('.price-slider-shell').forEach(function (shell) {
      var fill = shell.querySelector('.price-slider-fill');
      var minSlider = shell.querySelector('[data-price-min]');
      var maxSlider = shell.querySelector('[data-price-max]');

      if (!fill || !minSlider || !maxSlider) return;

      var minBound = parseFloat(minSlider.min || 0);
      var maxBound = parseFloat(maxSlider.max || 0);
      var minValue = parseFloat(minSlider.value || minBound);
      var maxValue = parseFloat(maxSlider.value || maxBound);
      var span = Math.max(maxBound - minBound, 1);
      var start = ((minValue - minBound) / span) * 100;
      var end = ((maxValue - minBound) / span) * 100;

      fill.style.left = start + '%';
      fill.style.right = (100 - end) + '%';
    });
  }

  function updateLayoutMetrics(context) {
    var root = document.documentElement;
    var headerGroup = document.querySelector('.shopify-section-group-header-group');
    var announcementBar = document.querySelector('.announcement');
    var siteHeader = document.querySelector('.site-header');
    var headerMain = document.querySelector('.header-main');
    var categoryStrip = document.querySelector('.category-strip');
    var shopRoot = context && context.closest ? context.closest('#shop, [data-home-shop-shell], .collection-shell') : null;
    var shopSection = shopRoot && shopRoot.matches ? shopRoot : document.getElementById('shop');
    var shopStyles = shopSection ? window.getComputedStyle(shopSection) : null;
    var headerStack = (announcementBar ? announcementBar.getBoundingClientRect().height : 0)
      + (headerMain ? headerMain.getBoundingClientRect().height : 0)
      + (categoryStrip ? categoryStrip.getBoundingClientRect().height : 0);
    var fallbackHeader = headerGroup ? headerGroup.getBoundingClientRect().height : (siteHeader ? siteHeader.getBoundingClientRect().height : 0);
    var stackOffset = Math.round(headerStack || fallbackHeader || 0);
    var shopPaddingTop = shopStyles ? parseFloat(shopStyles.paddingTop) || 0 : 0;
    var shopPaddingBottom = shopStyles ? parseFloat(shopStyles.paddingBottom) || 0 : 0;
    var extraOffset = 40;

    if (window.innerWidth <= 480) {
      extraOffset = 20;
    } else if (window.innerWidth <= 600) {
      extraOffset = 24;
    } else if (window.innerWidth <= 768) {
      extraOffset = 28;
    } else if (window.innerWidth <= 1024) {
      extraOffset = 32;
    }

    var desktopTop = Math.round(stackOffset + shopPaddingTop);
    var desktopMaxHeight = Math.max(280, Math.round(window.innerHeight - stackOffset - shopPaddingTop - shopPaddingBottom));
    var drawerMaxHeight = Math.max(280, Math.round(window.innerHeight - stackOffset));

    root.style.setProperty('--filter-stack-offset', stackOffset + 'px');
    root.style.setProperty('--filter-rail-top', desktopTop + 'px');
    root.style.setProperty('--filter-rail-max-height', desktopMaxHeight + 'px');
    root.style.setProperty('--filter-drawer-max-height', drawerMaxHeight + 'px');
    root.style.setProperty('--shop-scroll-offset', Math.round(stackOffset + extraOffset) + 'px');
  }

  function initFilterDrawer(root) {
    var toggle = root.querySelector('#mobile-filter-toggle');
    var shell = root.querySelector('#mobile-filter-shell');
    var backdrop = root.querySelector('#mobile-filter-backdrop');
    var closeBtn = root.querySelector('#mobile-filter-close');

    if (!toggle || !shell || toggle.dataset.collectionUiBound === 'true') return;

    function openDrawer() {
      shell.removeAttribute('hidden');
      document.body.classList.add('filter-drawer-open');
      toggle.setAttribute('aria-expanded', 'true');
      updateLayoutMetrics(root);
    }

    function closeDrawer() {
      shell.setAttribute('hidden', '');
      document.body.classList.remove('filter-drawer-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    function scrollToProductSection() {
      var heading = root.querySelector('#section-heading') || document.getElementById('section-heading');
      var offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--shop-scroll-offset')) || 90;

      if (!heading) return;

      window.scrollTo({
        top: Math.max(0, heading.getBoundingClientRect().top + window.scrollY - offset),
        behavior: 'smooth'
      });
    }

    toggle.addEventListener('click', openDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    root.querySelectorAll('[data-filter-done]').forEach(function (button) {
      if (button.dataset.collectionDoneBound === 'true') return;

      button.addEventListener('click', function () {
        if (shell && !shell.hasAttribute('hidden')) {
          closeDrawer();
        }
        scrollToProductSection();
      });

      button.dataset.collectionDoneBound = 'true';
    });

    if (root.dataset.collectionEscapeBound !== 'true') {
      root.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeDrawer();
      });
      root.dataset.collectionEscapeBound = 'true';
    }

    toggle.dataset.collectionUiBound = 'true';
  }

  function initPriceControls(root, options) {
    var isHomepageShell = !!(options && options.allowNavigation === false);

    refreshPriceLabels(root);
    refreshPriceFills(root);

    root.querySelectorAll('#price-min-desktop, #price-max-desktop, #price-min-mobile, #price-max-mobile').forEach(function (slider) {
      if (slider.dataset.collectionPriceBound === 'true') return;

      slider.addEventListener('input', function () {
        refreshPriceLabels(root);
        refreshPriceFills(root);
      });

      if (!isHomepageShell) {
        slider.addEventListener('change', function () {
          var form = slider.closest('[data-collection-filter-form]');
          if (!form) return;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });
      }

      slider.dataset.collectionPriceBound = 'true';
    });
  }

  function initCollectionSearch(root) {
    var searchInputs = root.querySelectorAll('[data-collection-search]');
    var productCards = root.querySelectorAll('.product-grid .product-card');
    var productCount = root.querySelector('.shop-count');
    var noResults = root.querySelector('#no-results');

    if (!searchInputs.length || !productCards.length) return;

    function applySearch(query) {
      var normalizedQuery = String(query || '').trim().toLowerCase();
      var visibleCount = 0;

      if (productCount && !productCount.dataset.defaultCount) {
        productCount.dataset.defaultCount = productCount.textContent.trim();
      }

      productCards.forEach(function (card) {
        var searchText = card.getAttribute('data-search-text') || card.textContent || '';
        var isMatch = !normalizedQuery || searchText.toLowerCase().indexOf(normalizedQuery) !== -1;

        card.style.display = isMatch ? '' : 'none';
        if (isMatch) visibleCount += 1;
      });

      if (productCount) {
        productCount.textContent = normalizedQuery
          ? 'Showing ' + visibleCount + ' product' + (visibleCount === 1 ? '' : 's')
          : (productCount.dataset.defaultCount || productCount.textContent);
      }

      if (noResults) {
        noResults.style.display = visibleCount === 0 ? 'block' : 'none';
      }

      if (window.AlishbaStorefront && typeof window.AlishbaStorefront.hydrateVisibleProductImages === 'function') {
        window.AlishbaStorefront.hydrateVisibleProductImages(root);
      }
    }

    searchInputs.forEach(function (input) {
      if (input.dataset.collectionSearchBound === 'true') return;

      input.addEventListener('input', function () {
        searchInputs.forEach(function (otherInput) {
          if (otherInput !== input) otherInput.value = input.value;
        });
        applySearch(input.value);
      });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });

      input.dataset.collectionSearchBound = 'true';
    });

    applySearch(searchInputs[0].value);
  }

  function initStandaloneNavigation(root, options) {
    var allowNavigation = !options || options.allowNavigation !== false;
    if (!allowNavigation) return;

    root.querySelectorAll('[data-collection-filter-form]').forEach(function (form) {
      if (form.dataset.collectionNavBound === 'true') return;

      form.addEventListener('change', function (event) {
        if (event.target.matches('[data-filter-checkbox]')) {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        }
      });

      form.dataset.collectionNavBound = 'true';
    });

    root.querySelectorAll('#SortBy, [data-sort-select]').forEach(function (select) {
      if (select.dataset.collectionSortBound === 'true') return;

      select.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', select.value);
        params.delete('page');
        window.location.search = params.toString();
      });

      select.dataset.collectionSortBound = 'true';
    });
  }

  function initFilterGroupAccordion(root) {
    root.querySelectorAll('.filter-group').forEach(function (group) {
      if (group.dataset.accordionBound === 'true') return;

      var body = group.querySelector('.filter-group-body');
      var head = group.querySelector('.filter-group-head');
      if (!body || !head) return;

      // All collapsed on init — height already 0 via CSS, just mark state
      group.classList.remove('is-open');

      head.addEventListener('click', function (event) {
        // Let link clicks (e.g. "Clear") navigate without toggling
        if (event.target.closest('a')) return;

        if (group.classList.contains('is-open')) {
          // Close: pin explicit height then animate to 0
          body.style.height = body.scrollHeight + 'px';
          body.getBoundingClientRect(); // force reflow
          body.style.height = '0';
          group.classList.remove('is-open');
        } else {
          // Open: animate from 0 to content height, then release to auto
          var targetHeight = body.scrollHeight;
          body.style.height = targetHeight + 'px';
          group.classList.add('is-open');
          body.addEventListener('transitionend', function handler() {
            if (group.classList.contains('is-open')) {
              body.style.height = 'auto';
            }
            body.removeEventListener('transitionend', handler);
          });
        }
      });

      group.dataset.accordionBound = 'true';
    });
  }

  function initCollectionUI(root, options) {
    var scope = root && root.nodeType === 1 ? root : document;
    var hasCollectionUi = scope.querySelector('.collection-shell, .category-layout, #mobile-filter-shell, [data-collection-filter-form]');

    if (!hasCollectionUi) return;

    initFilterGroupAccordion(scope);
    initFilterDrawer(scope);
    initPriceControls(scope, options);
    initCollectionSearch(scope);
    initStandaloneNavigation(scope, options);
    updateLayoutMetrics(scope);
  }

  window.AlishbaCollection = window.AlishbaCollection || {};
  window.AlishbaCollection.init = initCollectionUI;
  window.AlishbaCollection.refreshPriceLabels = refreshPriceLabels;
  window.AlishbaCollection.refreshPriceFills = refreshPriceFills;
  window.AlishbaCollection.updateLayoutMetrics = updateLayoutMetrics;

  document.addEventListener('DOMContentLoaded', function () {
    initCollectionUI(document, { allowNavigation: true });

    window.addEventListener('resize', function () {
      updateLayoutMetrics(document);
    });

    if ('ResizeObserver' in window) {
      var header = document.querySelector('.site-header');
      var shopRoot = document.getElementById('shop') || document.querySelector('.collection-shell');

      var observer = new ResizeObserver(function () {
        updateLayoutMetrics(document);
      });

      if (header) observer.observe(header);
      if (shopRoot) observer.observe(shopRoot);
    }
  });
})();
